import React, { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BarChart3,
  Target,
  Hammer,
  Tag,
  MoreHorizontal,
  X,
  Calculator,
  FileText,
  Briefcase,
  Users,
  Settings,
  ClipboardList,
  Building2,
  DollarSign,
  Activity,
  Shield,
  Kanban,
  Calendar,
  Camera,
  LayoutDashboard,
} from 'lucide-react';

// Bottom tabs — the 4 most-used pages + More
const BOTTOM_TABS = [
  { id: 'dashboard', path: '/', label: 'The Hub', icon: BarChart3 },
  { id: 'acquisition', path: '/acquisition', label: 'Acquisition', icon: Target },
  { id: 'rehab', path: '/rehab', label: 'Rehab', icon: Hammer },
  { id: 'selling', path: '/selling', label: 'Selling', icon: Tag },
];

// Everything else lives in the More drawer
const MORE_ITEMS = [
  { id: 'acq-pipeline', path: '/leads', label: 'Acq. Pipeline', icon: Kanban },
  { id: 'comps', path: '/comps', label: 'Comps', icon: Building2 },
  { id: 'rehab-pipeline', path: '/rehab-pipeline', label: 'Rehab Pipeline', icon: Kanban },
  { id: 'flip-analyzer', path: '/flip-analyzer', label: 'Flip Analyzer', icon: Calculator },
  { id: 'loan-calculator', path: '/loan-calculator', label: 'Loan Calculator', icon: DollarSign },
  {
    id: 'rehab-estimator',
    path: '/rehab-estimator',
    label: 'Rehab Estimator',
    icon: ClipboardList,
  },
  { id: 'selling-pipeline', path: '/selling-pipeline', label: 'Sell Pipeline', icon: Kanban },
  { id: 'listing-mgmt', path: '/selling/listings', label: 'Listings & Offers', icon: FileText },
  { id: 'scheduler', path: '/scheduler', label: 'Scheduler', icon: Calendar },
  { id: 'photos', path: '/photos', label: 'Photo Log', icon: Camera },
  { id: 'approvals', path: '/approvals', label: 'Approvals', icon: Shield },
  { id: 'analysis', path: '/analysis', label: 'Live Analysis', icon: Activity },
  { id: 'portfolio', path: '/portfolio', label: 'Portfolio', icon: Briefcase },
  { id: 'reports', path: '/reports', label: 'Reports', icon: FileText },
  { id: 'team', path: '/team', label: 'Team', icon: Users },
  { id: 'settings', path: '/settings', label: 'Settings', icon: Settings },
];

export default function MobileNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showMore, setShowMore] = useState(false);

  const isActive = useCallback(
    (path) => {
      if (path === '/') return location.pathname === '/';
      // Exact match for phase dashboard paths to prevent double-highlight
      if (path === '/selling' || path === '/rehab' || path === '/acquisition') {
        return location.pathname === path;
      }
      return location.pathname === path || location.pathname.startsWith(path + '/');
    },
    [location.pathname]
  );

  const handleNav = useCallback(
    (path) => {
      navigate(path);
      setShowMore(false);
    },
    [navigate]
  );

  return (
    <>
      {/* Bottom Tab Bar — mobile only */}
      <nav
        aria-label="Mobile navigation"
        className="mobile-nav md:hidden fixed bottom-0 left-0 right-0 z-50 flex"
      >
        {BOTTOM_TABS.map((tab) => {
          const Icon = tab.icon;
          const active = isActive(tab.path);
          return (
            <button
              key={tab.id}
              onClick={() => handleNav(tab.path)}
              className={`mobile-nav-item${active ? ' active' : ''}`}
            >
              <Icon size={22} />
              <span className="mobile-nav-label">{tab.label}</span>
            </button>
          );
        })}

        {/* More button */}
        <button
          onClick={() => setShowMore(true)}
          className={`mobile-nav-item${showMore ? ' active' : ''}`}
        >
          <MoreHorizontal size={22} />
          <span className="mobile-nav-label">More</span>
        </button>
      </nav>

      {/* More drawer overlay */}
      {showMore && (
        <div className="more-drawer-overlay md:hidden" onClick={() => setShowMore(false)} />
      )}

      {/* More drawer */}
      <div className={`more-drawer md:hidden ${showMore ? 'open' : ''}`}>
        {/* Handle + header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <div className="w-10 h-1 bg-slate-200 dark:bg-slate-600 rounded-full mx-auto absolute left-1/2 -translate-x-1/2 top-2" />
          <h3 className="text-base font-bold text-slate-800 dark:text-slate-100 mt-2">More</h3>
          <button
            onClick={() => setShowMore(false)}
            aria-label="Close menu"
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl"
          >
            <X size={18} className="text-slate-400" />
          </button>
        </div>

        {/* Grid of nav items */}
        <div className="grid grid-cols-3 gap-2 px-4 pb-8 pt-2 overflow-y-auto max-h-[60vh]">
          {MORE_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.id}
                onClick={() => handleNav(item.path)}
                className={`flex flex-col items-center justify-center gap-2 p-4 rounded-2xl transition-all ${
                  active
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600'
                    : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
              >
                <Icon size={22} />
                <span className="text-[11px] font-semibold text-center leading-tight">
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
